import { zodResolver } from '@hookform/resolvers/zod';
import { useAuth } from '@shared/auth';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Form,
  FormButton,
  FormTextField,
  Grid,
} from '@shared/components';
import { WithPermission } from '@shared/permissions';
import { useSnackbar } from '@shared/snackbar';
import { useInviteUserMutation } from '@user-management/graphql';
import { APP_PERMISSIONS } from '@user-management/permissions';
import { UserAppRoleSelection } from '@user-management/role-management';
import { FormProvider, useForm } from 'react-hook-form';

import {
  UserInvitationFormSchema,
  type UserInvitationFormInput,
} from './user-invitation-form-schema';

interface UserInvitationModalProps {
  open: boolean;
  onClose: () => void;
}

export default function UserInvitationModal(props: Readonly<UserInvitationModalProps>) {
  const { open: isOpen, onClose } = props;

  const { userProfile } = useAuth();
  const userAppRoles = userProfile?.appRoles ?? [];

  const { showPromiseSnackbar } = useSnackbar();

  const formContext = useForm<UserInvitationFormInput>({
    defaultValues: {
      firstName: '',
      lastName: '',
      email: '',
      appRoles: [],
    },
    resolver: zodResolver(UserInvitationFormSchema),
  });

  const [inviteUser] = useInviteUserMutation();

  const handleInviteUser = async (data: UserInvitationFormInput) => {
    const { appRoles = [], ...form } = data;

    const appRolesArray = appRoles
      .map((appRoleForm) => appRoleForm?.value)
      .filter((v) => typeof v !== 'undefined');

    await inviteUser({
      variables: {
        quickCreateInput: form,
        appRoleIds: { appRoleIds: appRolesArray },
      },
      refetchQueries: ['searchUsersByAppRoleFilters'],
      awaitRefetchQueries: true,
      onCompleted: (data) => {
        if (!data?.suQuickCreateUser?.success) {
          if (data?.suQuickCreateUser?.message?.includes('ER_DUP_ENTRY')) {
            throw new Error('Email already exists');
          } else {
            throw new Error('Failed to invite user');
          }
        }
      },
      onError: (error) => {
        if (error.message.includes('ER_DUP_ENTRY')) {
          formContext.setError('email', {
            message: 'อีเมลนี้มีอยู่ในระบบแล้ว กรุณาตรวจสอบหรือใช้ที่อยู่อีเมลอื่น',
          });
        } else {
          formContext.setError('root', {
            message: 'เกิดข้อผิดพลาด กรุณาลองใหม่ภายหลัง',
          });
        }

        throw error;
      },
    });
    onClose();
  };

  const handleResetForm = () => {
    formContext.reset();
  };

  const handleSubmit = (data: UserInvitationFormInput) => {
    showPromiseSnackbar({
      message: {
        success: 'User invited successfully',
        error: 'Failed to invite user',
        pending: 'Inviting user...',
      },
      promise: () => handleInviteUser(data),
    });
  };

  return (
    <FormProvider {...formContext}>
      <Dialog
        open={isOpen}
        onClose={onClose}
        slotProps={{
          paper: { component: Form, onSubmit: formContext.handleSubmit(handleSubmit) },
          transition: {
            onExited: handleResetForm,
          },
        }}
      >
        <DialogTitle onClose={onClose}>Invite User</DialogTitle>
        <DialogContent
          dividers
          sx={{ display: 'flex', flexDirection: 'column', gap: (theme) => theme.spacing(2) }}
        >
          {formContext.formState.errors.root?.message ? (
            <Alert severity='error'>{formContext.formState.errors.root?.message}</Alert>
          ) : null}
          <Grid
            sx={{
              gridTemplate: '1fr / 1fr 1fr',
              gap: (theme) => theme.spacing(4),
              gridAutoFlow: 'row dense',
            }}
          >
            <FormTextField
              name='firstName'
              label='ชื่อภาษาอังกฤษ'
              placeholder='กรุณากรอกชื่อภาษาอังกฤษ'
            />
            <FormTextField
              name='lastName'
              label='นามสกุลภาษาอังกฤษ'
              placeholder='กรุณากรอกนามสกุลภาษาอังกฤษ'
            />
            <FormTextField
              name='email'
              label='อีเมลผู้ใช้'
              placeholder='กรุณากรอกอีเมล'
              sx={{ gridColumn: '1 / -1' }}
            />
            <WithPermission
              requiredPermission={[
                APP_PERMISSIONS.SETTING.ROLE.UPDATE,
                APP_PERMISSIONS.SETTING.USER.UPDATE,
              ]}
              // TODO: Handle error element
            >
              <UserAppRoleSelection
                name='appRoles'
                label='บทบาทผู้ใช้ในระบบ'
                placeholder='กรุณาเลือกบทบาทผู้ใช้'
                sx={{ gridColumn: '1 / -1' }}
                valueKey='id'
                filterOptions={(v, state) => {
                  return v.filter(
                    ({ value }) =>
                      typeof value === 'string' &&
                      !userAppRoles.includes(value.toString()) &&
                      value?.toString().includes(state.inputValue),
                  );
                }}
              />
            </WithPermission>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button
            variant='outlined'
            color='secondary'
            size='large'
            onClick={onClose}
            disabled={formContext.formState.isSubmitting}
          >
            Cancel
          </Button>
          <FormButton size='large' label='Send Invite' />
        </DialogActions>
      </Dialog>
    </FormProvider>
  );
}
